import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import callTrainingInfoApi from '../apis/callTrainingInfoApi';
import useAccessKey from './useAccessKey';
import { TrainingInfoSentences } from '../types/apis/trainingInfoApi';
import useAudio from './useAudio';
import {
  sentenceIdFromCurrentTime,
  nextSentenceStartTime,
  prevSentenceCurrentTime,
  startTimeFromSentenceId,
} from '../utils/training/sentencesUtil';
import { trainingMode } from '../utils/training/trainingUtil';
import { AlertButton } from '../components/Templates/ElmerModalAlert';
import callSentenceStatusAPI from '../apis/callSentenceStatusAPI';
import useTrainingComplete from './useTrainingComplete';
import callTrainingStatusApi from '../apis/callTrainingStatusApi';
import setupCookie from '../utils/cookieUtil';
import useResume from './useResume';
import useCheckTransition from './useCheckTransition';
import useTrainingState from './useTrainingState';
import { PAGE_PATH_NAME } from '../utils/const';

// 本カスタムフックの返り値
export type UseTrainingValue = {
  // 再生時間
  currentTime: number;
  // API呼び出し中か
  isCallingApi: boolean;
  // センテンス一覧
  trainingSentences: TrainingInfoSentences[];
  // トレーニングモード
  mode: string;
  // モーダルクローズクリック
  onClickCloseModal: () => void;
  // トレーニング情報取得時のアラート
  trainingInfoAlert: TrainingInfoAlert;
  // Audio操作
  audioControl: AudioControl;
  // トレーニング完了ダイアログの値
  trainingCompleteDialogValue: TrainingCompleteDialogValue;
};

// トレーニング情報取得APIのresult_code
const TRAINING_INFO_API_RESULT_CODE = {
  // OK
  OK: 'OK',
  // 入力パラメータエラー
  WARN_INPUT_PARAM: 'WARN_INPUT_PARAM',
  // ログオフ状態
  INFO_LOGOFF: 'INFO_LOGOFF',
  // 不明なエラー
  ERR_UNKNOWN: 'ERR_UNKNOWN',
} as const;
type TRAINING_INFO_API_RESULT_CODE =
  typeof TRAINING_INFO_API_RESULT_CODE[keyof typeof TRAINING_INFO_API_RESULT_CODE];

// トレーニング情報取得エラーアラート一覧
export const TRAINING_INFO_ALERT_TYPES = {
  // 入力パラメータエラー
  WARN_INPUT_PARAM: 'WARN_INPUT_PARAM',
  // ログオフ状態
  INFO_LOGOFF: 'INFO_LOGOFF',
  // 不明なエラー
  ERR_UNKNOWN: 'ERR_UNKNOWN',
} as const;
type TRAINING_INFO_ALERT_TYPES =
  typeof TRAINING_INFO_ALERT_TYPES[keyof typeof TRAINING_INFO_ALERT_TYPES];

// 署名付きCookieのキー
const SIGNED_COOKIE_KEY = {
  // Policy
  POLICY: 'CloudFront-Policy',
  // KeyPairId
  KEY_PAIR_ID: 'CloudFront-Key-Pair-Id',
  // Signature
  SIGNATURE: 'CloudFront-Signature',
};
type SIGNED_COOKIE_KEY =
  typeof SIGNED_COOKIE_KEY[keyof typeof SIGNED_COOKIE_KEY];

// トレーニング情報取得APIアラート
export type TrainingInfoAlert = {
  // nullの場合、アラートは非表示
  type: TRAINING_INFO_ALERT_TYPES | null;
  // alertのタイトル
  alertTitle: string;
  // alertの文言
  alertText: string;
  // ボタン押下時の処理のプロパティ
  alertButtons: Array<AlertButton>;
};

// 音声再生時のエラーアラート
export type AudioErrorAlert = {
  // alertのタイトル
  alertTitle: string;
  // alertの文言
  alertText: string;
  // ボタン押下時の処理のプロパティ
  alertButtons: Array<AlertButton>;
};

/**
 * Audio関連のコントロール
 */
export type AudioControl = {
  // 再生
  play: () => void;
  // 一時停止
  pause: () => void;
  // 再生中か
  isPlaying: boolean;
  // ループ変更
  toggleLoop: () => void;
  // ループ中か
  isLoop: boolean;
  // トピックの先頭
  leadTopic: () => void;
  // トピックの完了
  completeTopic: () => void;
  // 再生速度
  playBackRate: number;
  // 再生速度変更
  changePlayBackRate: (rate: number) => void;
  // 次のセンテンス
  nextSentence: () => void;
  // 前のセンテンス
  prevSentence: () => void;
  // 終了したかどうか
  isEnded: boolean;
  // エラーが発生したかどうか
  isError: boolean;
  // 音声再生エラーアラート
  audioErrorAlert: AudioErrorAlert;
};

// トレーニング完了ダイアログの値
export type TrainingCompleteDialogValue = {
  // completeCheckboxの値
  completeCheckboxChecked: boolean;
  // completeCheckbox変更時の処理
  onChangeCompleteCheckbox: (event: ChangeEvent<HTMLInputElement>) => void;
  // completeCheckboxの値が見えるか
  isCompleteCheckboxVisible: boolean;
  // Next ボタンのタイトル
  nextTrainingButtonTitle: string;
  // close
  onClickCloseCompleteDialog: () => void;
  // repeat
  onClickRepeat: () => void;
  // Next Mode / Next Topic ボタン押下時の処理
  onClickNextTraining: () => void;
  // Topic Select ボタン押下時の処理
  onClickTopicSelect: () => void;
};

const useTraining = (): UseTrainingValue => {
  /** state */
  // アクセスキー
  const { accessKey } = useAccessKey();
  // ページロード中かどうか
  const [isCallingApi, setIsCallingApi] = useState<boolean>(false);
  // トレーニングのセンテンス
  const [trainingSentences, setTrainingSentences] = useState<
    TrainingInfoSentences[]
  >([]);
  // AudioのURL
  const [audioUrl, setAudioUrl] = useState<string>('');
  // アラート状態
  const [trainingInfoAlertType, setTrainingInfoAlertType] =
    useState<TRAINING_INFO_ALERT_TYPES | null>(null);
  // Audio
  // useCallBackで依存関係を単体の変数orメソッドにするため、全て展開する
  const {
    play,
    pause,
    prepareReplay,
    leadAudio,
    replay,
    changeStartCurrentTime,
    changeCurrentTime,
    completeAudio,
    currentTime,
    isFirstPlayed,
    changeIsError,
    isError,
    loadAudio,
    isPlaying,
    isLoop,
    playBackRate,
    toggleLoop,
    isEnded,
    changePlayBackRate,
  } = useAudio();

  // CompleteDialog
  // useCallBackで依存関係を単体の変数orメソッドにするため、全て展開する
  const {
    completeCheckboxChecked,
    topicSelect,
    nextTraining,
    changeCompleteCheckbox,
    isCompleteCheckboxVisible,
    onChangeCompleteCheckbox,
    nextTrainingButtonTitle,
  } = useTrainingComplete();
  // history
  const history = useHistory();
  // state
  const { trainingId } = useTrainingState();
  // Resume保存
  const { saveSentenceId, saveTrainingId } = useResume();
  // 遷移許可
  const { allowTransition } = useCheckTransition();

  /**
   * トレーニング情報取得処理
   */
  const fetchTrainingInfo = useCallback(
    (accesskey: string, trainingID: number): void => {
      setIsCallingApi(true);

      callTrainingInfoApi({ accessKey: accesskey, trainingId: trainingID })
        .then((res) => {
          if (res.resultCode !== TRAINING_INFO_API_RESULT_CODE.OK) {
            if (
              res.resultCode === TRAINING_INFO_API_RESULT_CODE.WARN_INPUT_PARAM
            ) {
              throw TRAINING_INFO_ALERT_TYPES.WARN_INPUT_PARAM;
            } else if (
              res.resultCode === TRAINING_INFO_API_RESULT_CODE.INFO_LOGOFF
            ) {
              throw TRAINING_INFO_ALERT_TYPES.INFO_LOGOFF;
            } else {
              throw TRAINING_INFO_ALERT_TYPES.ERR_UNKNOWN;
            }
          }

          // 署名付きCookie設定
          setupCookie(SIGNED_COOKIE_KEY.POLICY, res.audio.policy, '');
          setupCookie(SIGNED_COOKIE_KEY.KEY_PAIR_ID, res.audio.keyPairId, '');
          setupCookie(SIGNED_COOKIE_KEY.SIGNATURE, res.audio.signature, '');

          // センテンス再生状況保存
          void callSentenceStatusAPI({
            accesskey,
            trainingId: trainingID,
            sentenceId: res.sentenceId
              ? res.sentenceId
              : res.sentences[0].english.id,
          }).finally(() => {
            setIsCallingApi(false);
          });

          setTrainingSentences(res.sentences);
          changeStartCurrentTime(
            startTimeFromSentenceId(res.sentenceId, res.sentences),
          );
          changeCompleteCheckbox(res.isCompleted);
          setAudioUrl(res.audio.url);
        })
        .catch((e: TRAINING_INFO_ALERT_TYPES | unknown) => {
          if (
            e === TRAINING_INFO_ALERT_TYPES.WARN_INPUT_PARAM ||
            e === TRAINING_INFO_ALERT_TYPES.INFO_LOGOFF ||
            e === TRAINING_INFO_ALERT_TYPES.ERR_UNKNOWN
          ) {
            setTrainingInfoAlertType(e);
          } else {
            setTrainingInfoAlertType(TRAINING_INFO_ALERT_TYPES.ERR_UNKNOWN);
          }
          setIsCallingApi(false);
        });
    },
    [changeStartCurrentTime, changeCompleteCheckbox],
  );

  /** useEffect */

  useEffect(() => {
    if (!allowTransition()) {
      return;
    }
    saveTrainingId(trainingId);

    fetchTrainingInfo(accessKey, trainingId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * トレーニングのモードを返す
   */
  const mode = useMemo((): string => trainingMode(trainingId), [trainingId]);

  /**
   * モーダルウインドウを閉じる
   */
  const onClickCloseModal = useCallback(() => {
    setTrainingInfoAlertType(null);
  }, []);

  /**
   * トレーニング情報取得アラートボタン
   */
  const alertButtons = useMemo(() => {
    switch (trainingInfoAlertType) {
      case TRAINING_INFO_ALERT_TYPES.WARN_INPUT_PARAM:
      case TRAINING_INFO_ALERT_TYPES.ERR_UNKNOWN:
        return [
          {
            label: '閉じる',
            onClick: () => {
              setTrainingInfoAlertType(null);
              history.push(PAGE_PATH_NAME.LESSON);
            },
          },
        ];
      default:
        return [];
    }
  }, [history, trainingInfoAlertType]);

  /**
   * トレーニング情報取得アラート文言
   */
  const alertText = useMemo(() => {
    switch (trainingInfoAlertType) {
      case TRAINING_INFO_ALERT_TYPES.WARN_INPUT_PARAM:
        return 'トレーニング情報取得に失敗しました。\nお手数ですが、時間をおいて再度お試しください。';
      case TRAINING_INFO_ALERT_TYPES.ERR_UNKNOWN:
        return 'トレーニング情報取得時に不明なエラーが発生しました。\nお手数ですが、時間をおいて再度お試しください。';
      default:
        return '';
    }
  }, [trainingInfoAlertType]);

  /**
   * トレーニング情報取得アラートタイトル
   */
  const alertTitle = useMemo(() => {
    if (trainingInfoAlertType) {
      return 'トレーニング情報取得エラー';
    }

    return '';
  }, [trainingInfoAlertType]);

  /** Audio */

  /**
   * 再生
   */
  const audioPlay = useCallback(() => {
    // センテンス再生状況保存
    void callSentenceStatusAPI({
      accesskey: accessKey,
      trainingId,
      sentenceId: sentenceIdFromCurrentTime(currentTime, trainingSentences),
    });
    play();
  }, [accessKey, trainingId, trainingSentences, currentTime, play]);

  /**
   * 一時停止
   */
  const audioPause = useCallback(() => {
    // センテンス再生状況保存
    void callSentenceStatusAPI({
      accesskey: accessKey,
      trainingId,
      sentenceId: sentenceIdFromCurrentTime(currentTime, trainingSentences),
    });
    pause();
  }, [accessKey, trainingId, currentTime, trainingSentences, pause]);

  /**
   * 次のセンテンス
   * @param audioCurerntTime 現在の再生時間
   */
  const nextSentence = useCallback(() => {
    // 再生していない場合は、何もしない
    if (!isFirstPlayed) {
      return;
    }
    const time = nextSentenceStartTime(currentTime, trainingSentences);
    // 最後のセンテンス再生中 or 再生後なら、トピック完了扱いにする
    if (time === 0) {
      completeAudio();
    } else {
      changeCurrentTime(time);
    }
    const sentenceId = sentenceIdFromCurrentTime(time, trainingSentences);
    void callSentenceStatusAPI({
      accesskey: accessKey,
      trainingId,
      sentenceId,
    });
  }, [
    accessKey,
    changeCurrentTime,
    completeAudio,
    isFirstPlayed,
    trainingId,
    trainingSentences,
    currentTime,
  ]);

  /**
   * 前のセンテンス
   * @param audioCurerntTime 現在の再生時間
   */
  const prevSentence = useCallback(() => {
    // 再生していない場合は、何もしない
    if (!isFirstPlayed) {
      return;
    }
    const time = prevSentenceCurrentTime(currentTime, trainingSentences);
    changeCurrentTime(time);
    const sentenceId = sentenceIdFromCurrentTime(time, trainingSentences);
    void callSentenceStatusAPI({
      accesskey: accessKey,
      trainingId,
      sentenceId,
    });
  }, [
    isFirstPlayed,
    changeCurrentTime,
    trainingSentences,
    accessKey,
    trainingId,
    currentTime,
  ]);

  /**
   * トピックの先頭
   */
  const leadTopic = useCallback(() => {
    leadAudio();
    void callSentenceStatusAPI({
      accesskey: accessKey,
      trainingId,
      sentenceId: trainingSentences[0].english.id,
    });
  }, [accessKey, trainingId, trainingSentences, leadAudio]);

  /**
   * 音声再生時のエラーアラートテキスト
   */
  const audioErrorAlertText = useMemo(() => {
    if (isError) {
      return '音声再生中にエラーが発生しました。';
    }

    return '';
  }, [isError]);

  /**
   * 音声再生時のエラーアラートのタイトル
   */
  const audioErrorAlertTitle = useMemo(() => {
    if (isError) {
      return '音声再生エラー';
    }

    return '';
  }, [isError]);

  /**
   * 音声再生時のエラーアラートボタン
   */
  const audioErrorAlertButtons = useMemo(() => {
    if (isError) {
      return [
        {
          label: 'トピック選択',
          onClick: () => {
            changeIsError(false);
            history.push(PAGE_PATH_NAME.LESSON);
          },
        },
        {
          label: 'リロード',
          onClick: () => {
            changeIsError(false);
            history.go(0);
          },
        },
      ];
    }

    return [];
  }, [history, isError, changeIsError]);

  useEffect(() => {
    // 念の為、音声を止める
    pause();
  }, [isError, pause]);

  // urlが変わったら読み込み直す
  useEffect(() => {
    // 空文字 or nullなら何もしない
    if (!audioUrl) {
      return;
    }
    // 読み込み
    loadAudio(audioUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl]);

  useEffect(() => {
    if (!trainingSentences) {
      return;
    }
    saveSentenceId(sentenceIdFromCurrentTime(currentTime, trainingSentences));
  }, [currentTime, saveSentenceId, trainingSentences]);

  /** 音声操作 */
  const audioControl: AudioControl = {
    play: audioPlay,
    pause: audioPause,
    isPlaying,
    toggleLoop,
    isLoop,
    leadTopic,
    completeTopic: completeAudio,
    playBackRate,
    changePlayBackRate,
    nextSentence,
    prevSentence,
    isEnded,
    isError,
    audioErrorAlert: {
      alertTitle: audioErrorAlertTitle,
      alertText: audioErrorAlertText,
      alertButtons: audioErrorAlertButtons,
    },
  };

  /** トレーニング完了ダイアログ */

  /**
   * Closeボタン押下時の処理
   */
  const onClickCloseCompleteDialog = useCallback(() => {
    prepareReplay();
  }, [prepareReplay]);

  /**
   * Repeatボタン押下時の処理
   */
  const onClickRepeat = useCallback(() => {
    replay();
    void callSentenceStatusAPI({
      accesskey: accessKey,
      trainingId,
      sentenceId: trainingSentences[0].english.id,
    });
  }, [accessKey, trainingId, trainingSentences, replay]);

  /**
   * Next Topic / Next Mode ボタン押下時の処理
   */
  const onClickNextTraining = useCallback(() => {
    setIsCallingApi(true);
    // トレーニング状況設定API呼び出し(呼び出し結果は見ない)
    void callTrainingStatusApi({
      accesskey: accessKey,
      trainingId,
      isCompleted: completeCheckboxChecked,
    })
      .then()
      .catch()
      .finally(() => {
        setIsCallingApi(false);
        nextTraining();
      });
  }, [accessKey, trainingId, completeCheckboxChecked, nextTraining]);

  /**
   * TopicSelectボタン押下時の処理
   */
  const onClickTopicSelect = useCallback(() => {
    setIsCallingApi(true);
    void callTrainingStatusApi({
      accesskey: accessKey,
      trainingId,
      isCompleted: completeCheckboxChecked,
    })
      .then()
      .catch()
      .finally(() => {
        setIsCallingApi(false);
        topicSelect();
      });
  }, [accessKey, trainingId, completeCheckboxChecked, topicSelect]);

  /**
   * トレーニング完了ダイアログ
   */
  const trainingCompleteDialogValue: TrainingCompleteDialogValue = {
    isCompleteCheckboxVisible,
    onClickTopicSelect,
    onChangeCompleteCheckbox,
    onClickNextTraining,
    onClickRepeat,
    onClickCloseCompleteDialog,
    completeCheckboxChecked,
    nextTrainingButtonTitle,
  };

  return {
    currentTime,
    isCallingApi,
    onClickCloseModal,
    trainingSentences,
    mode,
    trainingInfoAlert: {
      type: trainingInfoAlertType,
      alertTitle,
      alertText,
      alertButtons,
    },
    audioControl,
    trainingCompleteDialogValue,
  };
};

export default useTraining;
